import React, { useState } from 'react';
import { BookOpen, ChevronLeft, Search, ExternalLink, Info } from 'lucide-react';
import { NavLink } from 'react-router-dom';

interface MetricDefinition {
  name: string;
  description: string;
  scoreRange: string;
  source: string;
  sourceUrl?: string;
  detailedExplanation: string;
  useCase: string;
  interpretation: string;
}

interface GlossaryProps {
  addNotification: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void;
}

const Glossary: React.FC<GlossaryProps> = ({ addNotification }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMetric, setSelectedMetric] = useState<number | null>(null);

  const metrics: MetricDefinition[] = [
    // RAGAS Evaluation Metrics
    {
      name: 'Faithfulness',
      description: 'Measures how well the answer follows the provided context without hallucination.',
      scoreRange: '0.0-1.0',
      source: 'RAGAS Framework',
      sourceUrl: 'https://docs.ragas.io/en/latest/concepts/metrics/faithfulness.html',
      detailedExplanation: 'RAGAS Faithfulness evaluates the extent to which the generated answer is grounded in the provided context. It specifically measures whether the answer contains information that contradicts or cannot be inferred from the context.',
      useCase: 'Essential for RAG systems to ensure answers are factually consistent with retrieved documents.',
      interpretation: 'Scores closer to 1.0 indicate high faithfulness. Scores below 0.5 suggest significant hallucination or contradiction.'
    },
    {
      name: 'Answer Relevancy',
      description: 'Measures how relevant the answer is to the question.',
      scoreRange: '0.0-1.0',
      source: 'RAGAS Framework',
      sourceUrl: 'https://docs.ragas.io/en/latest/concepts/metrics/answer_relevancy.html',
      detailedExplanation: 'Answer Relevancy evaluates whether the generated answer directly addresses the question asked. It measures the semantic alignment between the question and the answer.',
      useCase: 'Critical for ensuring RAG systems provide answers that are on-topic and directly address user queries.',
      interpretation: 'Scores closer to 1.0 indicate high relevancy. Scores below 0.5 suggest the answer is largely irrelevant to the question.'
    },
    {
      name: 'Context Precision',
      description: 'Measures how much of the provided context is relevant to the question.',
      scoreRange: '0.0-1.0',
      source: 'RAGAS Framework',
      sourceUrl: 'https://docs.ragas.io/en/latest/concepts/metrics/context_precision.html',
      detailedExplanation: 'Context Precision evaluates the quality of the retrieved context by measuring how much of it is actually relevant to answering the question. It penalizes retrieval of irrelevant information.',
      useCase: 'Important for evaluating the quality of the retrieval component in RAG systems.',
      interpretation: 'Scores closer to 1.0 indicate high precision. Scores below 0.5 suggest significant irrelevant information in the context.'
    },
    {
      name: 'Context Recall',
      description: 'Measures how well the context covers the information needed to answer the question.',
      scoreRange: '0.0-1.0',
      source: 'RAGAS Framework',
      sourceUrl: 'https://docs.ragas.io/en/latest/concepts/metrics/context_recall.html',
      detailedExplanation: 'Context Recall evaluates whether the retrieved context contains all the necessary information to answer the question completely. It measures the completeness of the retrieved information.',
      useCase: 'Essential for ensuring the retrieval component provides sufficient information for accurate answering.',
      interpretation: 'Scores closer to 1.0 indicate high recall. Scores below 0.5 suggest important information is missing from the context.'
    },
    {
      name: 'Answer Correctness',
      description: 'Measures the factual correctness and accuracy of the answer.',
      scoreRange: '0.0-1.0',
      source: 'RAGAS Framework',
      sourceUrl: 'https://docs.ragas.io/en/latest/concepts/metrics/answer_correctness.html',
      detailedExplanation: 'Answer Correctness evaluates the factual accuracy of the generated answer based on the provided context. It considers correctness, completeness, and clarity of the answer.',
      useCase: 'Critical for ensuring RAG systems provide factually accurate and correct information.',
      interpretation: 'Scores closer to 1.0 indicate high correctness. Scores below 0.5 suggest significant factual errors or inaccuracies.'
    },
    {
      name: 'Answer Similarity',
      description: 'Measures semantic similarity between expected and actual answers.',
      scoreRange: '0.0-1.0',
      source: 'RAGAS Framework',
      sourceUrl: 'https://docs.ragas.io/en/latest/concepts/metrics/answer_similarity.html',
      detailedExplanation: 'Answer Similarity evaluates how semantically similar the generated answer is to what would be expected based on the context. It focuses on meaning rather than exact word matching.',
      useCase: 'Useful for comparing generated answers against reference answers or expected responses.',
      interpretation: 'Scores closer to 1.0 indicate high semantic similarity. Scores below 0.5 suggest significant semantic differences.'
    },
    {
      name: 'Context Relevancy',
      description: 'Measures how relevant the context is to the question.',
      scoreRange: '0.0-1.0',
      source: 'RAGAS Framework',
      sourceUrl: 'https://docs.ragas.io/en/latest/concepts/metrics/context_relevancy.html',
      detailedExplanation: 'Context Relevancy evaluates whether the retrieved context is relevant and useful for answering the question. It measures the appropriateness of the retrieved information.',
      useCase: 'Important for evaluating the quality of context retrieval in RAG systems.',
      interpretation: 'Scores closer to 1.0 indicate high context relevancy. Scores below 0.5 suggest the context is largely irrelevant to the question.'
    }
  ];

  const filteredMetrics = metrics.filter(metric => {
    const matchesSearch = metric.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         metric.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'RAGAS':
        return 'bg-green-900/50 text-green-200 border-green-700';
      default:
        return 'bg-slate-600 text-slate-100 border-slate-700';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <NavLink
              to="/resource"
              className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
              title="Back to Resource Center"
            >
              <ChevronLeft className="h-5 w-5 text-slate-300" />
            </NavLink>
            <BookOpen className="h-8 w-8 text-purple-400" />
            <div>
              <h1 className="text-2xl font-bold text-slate-100">Evaluation Metrics Glossary</h1>
              <p className="text-slate-300 mt-1">Comprehensive definitions of LLM evaluation metrics used in HIVEAIASSURE</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search RAGAS metrics..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Metrics Table */}
      <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-700">
            <thead className="bg-slate-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Metric
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Score Range
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Source
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-slate-800 divide-y divide-slate-700">
              {filteredMetrics.map((metric, index) => (
                <tr key={index} className="hover:bg-slate-700/50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-slate-100">{metric.name}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-slate-100 max-w-xs">{metric.description}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-slate-100">{metric.scoreRange}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-1">
                      <span className="text-sm text-slate-100">{metric.source}</span>
                      {metric.sourceUrl && (
                        <a
                          href={metric.sourceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-purple-400 hover:text-purple-300"
                          title="View source documentation"
                        >
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => setSelectedMetric(selectedMetric === index ? null : index)}
                      className="text-purple-400 hover:text-purple-300"
                    >
                      {selectedMetric === index ? 'Hide Details' : 'View Details'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detailed Information Modal/Expanded Rows */}
      {selectedMetric !== null && (
        <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-100">
              {filteredMetrics[selectedMetric]?.name} - Detailed Information
            </h3>
            <button
              onClick={() => setSelectedMetric(null)}
              className="text-slate-400 hover:text-slate-300"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {filteredMetrics[selectedMetric] && (
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium text-slate-100 mb-2">Detailed Explanation</h4>
                <p className="text-sm text-slate-300">{filteredMetrics[selectedMetric].detailedExplanation}</p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-slate-100 mb-2">Use Case</h4>
                <p className="text-sm text-slate-300">{filteredMetrics[selectedMetric].useCase}</p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-slate-100 mb-2">Interpretation</h4>
                <p className="text-sm text-slate-300">{filteredMetrics[selectedMetric].interpretation}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* No Results */}
      {filteredMetrics.length === 0 && (
        <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-12 text-center">
          <Info className="h-12 w-12 text-slate-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-100 mb-2">No metrics found</h3>
          <p className="text-slate-300">Try adjusting your search terms or filter criteria.</p>
        </div>
      )}

      {/* Summary Stats */}
      <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
        <h3 className="text-lg font-semibold text-slate-100 mb-4">Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="text-center p-4 bg-green-900/50 rounded-lg border border-green-700/50">
            <div className="text-2xl font-bold text-green-200">{metrics.length}</div>
            <div className="text-sm text-slate-300">RAGAS Metrics</div>
          </div>
          <div className="text-center p-4 bg-blue-900/50 rounded-lg border border-blue-700/50">
            <div className="text-2xl font-bold text-blue-200">1</div>
            <div className="text-sm text-slate-300">Evaluation Framework</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Glossary;
